import { createTheme } from '@mui/material/styles'

/* ===========================================
   BRAND COLORS - Synced with CSS variables in index.css
   Keep these in sync with :root variables!
   =========================================== */
const brandColors = {
  // Primary - Brand Violet
  primary: '#5c4b99',
  primaryLight: '#7b6baf',
  primaryDark: '#3d2d7a',
  
  // Secondary - Navy (from logo)
  navy: '#2d2855',
  
  // Success - Save actions (modern green)
  success: '#16a34a',
  successLight: '#22c55e',
  successDark: '#15803d',
  
  // Warning
  warning: '#d97706',
  warningLight: '#f59e0b',
  warningDark: '#b45309',
  
  // Error - Delete/danger actions
  error: '#dc2626',
  errorLight: '#ef4444',
  errorDark: '#b91c1c',
  
  // Backgrounds
  bgMain: '#f5f4f9',
  bgPaper: '#ffffff',
  bgSidebar: '#faf9fc',
  
  // Text
  textPrimary: '#2d2855',
  textSecondary: '#6b6b6b',
  textMuted: '#9a9a9a',
  
  // Border
  border: 'rgba(92, 75, 153, 0.08)',
}

// Export for use in components that need direct access
export { brandColors }

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brandColors.primary,
      light: brandColors.primaryLight,
      dark: brandColors.primaryDark,
      contrastText: '#fff',
    },
    secondary: {
      main: brandColors.navy,
      light: '#4a4570',
      dark: '#1e1a40',
    },
    success: {
      main: brandColors.success,
      light: brandColors.successLight,
      dark: brandColors.successDark,
      contrastText: '#fff',
    },
    warning: {
      main: brandColors.warning,
      light: brandColors.warningLight,
      dark: brandColors.warningDark,
    },
    error: {
      main: brandColors.error,
      light: brandColors.errorLight,
      dark: brandColors.errorDark,
    },
    background: {
      default: brandColors.bgMain,
      paper: brandColors.bgPaper,
    },
    text: {
      primary: brandColors.textPrimary,
      secondary: brandColors.textSecondary,
    },
    divider: brandColors.border,
  },
  typography: {
    fontFamily: [
      'DM Sans',
      'system-ui',
      '-apple-system',
      'BlinkMacSystemFont',
      'sans-serif',
    ].join(','),
    h4: {
      fontWeight: 600,
      fontSize: '1.875rem',
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.375rem',
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      letterSpacing: '-0.01em',
    },
    body1: {
      fontSize: '0.938rem',
      letterSpacing: '0.01em',
    },
    body2: {
      fontSize: '0.875rem',
      letterSpacing: '0.01em',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      letterSpacing: '0.02em',
    },
    caption: {
      fontSize: '0.75rem',
      letterSpacing: '0.06em',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 10,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgb(0 0 0 / 0.04)',
    '0 2px 4px 0 rgb(0 0 0 / 0.04), 0 1px 2px 0 rgb(0 0 0 / 0.02)',
    '0 4px 8px -2px rgb(0 0 0 / 0.06), 0 2px 4px -2px rgb(0 0 0 / 0.04)',
    '0 8px 16px -4px rgb(0 0 0 / 0.08), 0 4px 8px -4px rgb(0 0 0 / 0.04)',
    '0 12px 24px -6px rgb(0 0 0 / 0.1), 0 6px 12px -6px rgb(0 0 0 / 0.04)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
    '0 16px 32px -8px rgb(0 0 0 / 0.1)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 18px',
          boxShadow: 'none',
          fontWeight: 500,
          transition: 'all 0.15s ease',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: brandColors.primary,
          '&:hover': {
            backgroundColor: brandColors.primaryDark,
          },
        },
        containedSuccess: {
          backgroundColor: brandColors.success,
          '&:hover': {
            backgroundColor: brandColors.successDark,
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: 'rgba(92, 75, 153, 0.04)',
          },
        },
        outlinedPrimary: {
          borderColor: brandColors.primary,
          color: brandColors.primary,
          '&:hover': {
            borderColor: brandColors.primaryDark,
            backgroundColor: 'rgba(92, 75, 153, 0.04)',
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(92, 75, 153, 0.06)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04), 0 0 0 1px rgb(0 0 0 / 0.02)',
          borderRadius: 12,
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 8px 24px -8px rgb(0 0 0 / 0.12), 0 0 0 1px rgb(0 0 0 / 0.02)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            transition: 'all 0.2s ease',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: brandColors.primaryLight,
              },
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: '1.5px',
              },
            },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          padding: '10px 12px',
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: 'rgba(92, 75, 153, 0.06)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(92, 75, 153, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(92, 75, 153, 0.12)',
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 40,
          color: brandColors.textSecondary,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          letterSpacing: '0.02em',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 0 0 rgba(0, 0, 0, 0.06)',
        },
      },
    },
  },
})

export default theme

